import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Modal } from "../../components/common/Modal";
import { FormActions } from "../../components/common/FormActions";
import { useForm } from "../../hooks/useForm";
import { GeneralInfoSection } from "./sections/GeneralInfoSection";
import { NutritionSection } from "./sections/NutritionSection";
import { AvailabilitySection } from "./sections/AvailabilitySection";
import { CategorySection } from "./sections/CategorySection";
import { ImageUploadSection } from "./sections/ImageUploadSection";
import { TextInput } from "../../components/common/TextInput";
import { TextArea } from "../../components/common/TextArea";
import {
  fetchCategories,
  createMeal,
  updateMeal,
  deleteMeal,
} from "../../service/mealService";
import { getRestaurantId } from "../../utils/JwtUtil";

export function MealFormModal({ mealToEdit, onClose }) {
  const { t } = useTranslation();
  const restaurantId = getRestaurantId();
  const qc = useQueryClient();

  // fetch categories for dropdown
  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const isEdit = Boolean(mealToEdit);

  const initialValues = isEdit
    ? {
        nameEn: mealToEdit.nameEn,
        nameAr: mealToEdit.nameAr,
        descriptionEn: mealToEdit.descriptionEn,
        descriptionAr: mealToEdit.descriptionAr,
        price: mealToEdit.price.toString(),
        calories: mealToEdit.calories.toString(),
        isDaily: mealToEdit.isDaily,
        availableDays: mealToEdit.availableDays,
        mealTypes: mealToEdit.mealTypes,
        protein: mealToEdit.protein.toString(),
        carbs: mealToEdit.carbs.toString(),
        fat: mealToEdit.fat.toString(),
        categoryId: mealToEdit.categoryId.toString(),
        isListed: mealToEdit.isListed,
        imageUrl: mealToEdit.imageUrl || "",
      }
    : {
        nameEn: "",
        nameAr: "",
        descriptionEn: "",
        descriptionAr: "",
        price: "",
        calories: "",
        isDaily: false,
        availableDays: [],
        mealTypes: [],
        protein: "",
        carbs: "",
        fat: "",
        categoryId: "",
        isListed: false,
        imageUrl: "",
      };

  const { values, setField, reset } = useForm(initialValues);

  const mutationFn = isEdit
    ? (payload) => updateMeal(mealToEdit.id, payload)
    : (payload) => createMeal(payload);

  const mealMutation = useMutation({
    mutationFn: mutationFn,
    onSuccess: () => {
      qc.invalidateQueries(["meals", restaurantId]);
      reset();
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteMeal(id),
    onSuccess: () => {
      qc.invalidateQueries(["meals", restaurantId]);
      onClose();
    },
  });

  const handleDelete = () => {
    if (isEdit && mealToEdit) {
      const confirmMessage = t("deleteConfirmSingle", {
        name: mealToEdit.name,
      });
      if (window.confirm(confirmMessage)) {
        deleteMutation.mutate(mealToEdit.id);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    const requiredFields = {
      nameEn: values.nameEn.trim(),
      nameAr: values.nameAr.trim(),
      price: values.price,
      categoryId: values.categoryId,
    };

    // Check if any required field is empty
    const emptyFields = Object.entries(requiredFields).filter(
      ([key, value]) => !value
    );

    if (emptyFields.length > 0) {
      // You can add toast notification here if available
      console.error(
        "Please fill in all required fields:",
        emptyFields.map(([key]) => key)
      );
      return;
    }

    // Check if at least one meal type is selected
    if (!values.mealTypes || values.mealTypes.length === 0) {
      console.error("Please select at least one meal type");
      return;
    }

    const payload = {
      nameEn: values.nameEn,
      nameAr: values.nameAr,
      descriptionEn: values.descriptionEn,
      descriptionAr: values.descriptionAr,
      price: Number(values.price),
      calories: Number(values.calories),
      isDaily: values.isDaily,
      availableDays: values.availableDays,
      mealTypes: values.mealTypes,
      protein: Number(values.protein),
      carbs: Number(values.carbs),
      fat: Number(values.fat),
      categoryId: Number(values.categoryId),
      isListed: values.isListed,
      imageUrl: values.imageUrl,
      restaurantId,
      subscriptionIds: isEdit ? mealToEdit.subscriptionIds : [],
      sideIds: isEdit ? mealToEdit.sideIds : [],
    };
    mealMutation.mutate(payload);
  };

  return (
    <Modal
      title={isEdit ? t("editMeal") : t("createMeal")}
      onClose={onClose}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Top Section - Visibility and Daily Status */}
        <div className="bg-gray-50 p-6 rounded-lg border">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {t("mealStatus")}
          </h3>
          <div className="grid grid-cols-2 gap-6">
            {/* Meal Visibility Toggle */}
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-800">
                  {t("mealVisibility")}
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  {values.isListed
                    ? t("visibleToCustomers")
                    : t("hiddenFromCustomers")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setField("isListed", !values.isListed)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  values.isListed ? "bg-green-600" : "bg-gray-200"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    values.isListed ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {/* Daily Meal Toggle */}
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-800">
                  {t("isDailyMeal")}
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  {values.isDaily
                    ? t("availableEveryDay")
                    : t("availableOnSelectedDays")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setField("isDaily", !values.isDaily)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  values.isDaily ? "bg-blue-600" : "bg-gray-200"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    values.isDaily ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-2 gap-8 pb-20">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="bg-white p-6 rounded-lg border">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {t("basicInformation")}
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <TextInput
                    label={t("englishName")}
                    name="nameEn"
                    value={values.nameEn}
                    onChange={(e) => setField("nameEn", e.target.value)}
                    required
                  />
                  <TextInput
                    label={t("arabicName")}
                    name="nameAr"
                    value={values.nameAr}
                    onChange={(e) => setField("nameAr", e.target.value)}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <TextArea
                    label={t("englishDescription")}
                    name="descriptionEn"
                    value={values.descriptionEn}
                    onChange={(e) => setField("descriptionEn", e.target.value)}
                    rows={3}
                  />
                  <TextArea
                    label={t("arabicDescription")}
                    name="descriptionAr"
                    value={values.descriptionAr}
                    onChange={(e) => setField("descriptionAr", e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* General Information */}
            <div className="bg-white p-6 rounded-lg border">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {t("generalInformation")}
              </h3>
              <GeneralInfoSection values={values} setField={setField} />
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Nutrition Information */}
            <div className="bg-white p-6 rounded-lg border">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {t("nutritionInformation")}
              </h3>
              <NutritionSection
                protein={values.protein}
                carbs={values.carbs}
                fat={values.fat}
                setField={setField}
              />
            </div>

            {/* Availability Settings */}
            <div className="bg-white p-6 rounded-lg border">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {t("availabilitySettings")}
              </h3>
              <AvailabilitySection
                isDaily={values.isDaily}
                availableDays={values.availableDays}
                mealTypes={values.mealTypes}
                toggleDay={(d) =>
                  setField(
                    "availableDays",
                    values.availableDays.includes(d)
                      ? values.availableDays.filter((x) => x !== d)
                      : [...values.availableDays, d]
                  )
                }
                toggleType={(tpe) =>
                  setField(
                    "mealTypes",
                    values.mealTypes.includes(tpe)
                      ? values.mealTypes.filter((x) => x !== tpe)
                      : [...values.mealTypes, tpe]
                  )
                }
                setField={setField}
              />
            </div>
            {/* Category and Image */}
            <div className="bg-white p-6 rounded-lg border">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {t("categoryAndImage")}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <CategorySection
                  categories={categories}
                  categoryId={values.categoryId}
                  setField={setField}
                />
                <ImageUploadSection
                  imageUrl={values.imageUrl}
                  setField={setField}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Form Actions */}
        <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-6 mt-8 -mx-6 -mb-6">
          <div className="flex justify-between items-center">
            <div>
              {isEdit && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteMutation.isLoading}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {deleteMutation.isLoading
                    ? t("deleting") + "..."
                    : t("delete")}
                </button>
              )}
            </div>
            <div>
              <FormActions
                isLoading={mealMutation.isLoading}
                onCancel={onClose}
                submitLabel={isEdit ? t("save") : t("create")}
                cancelLabel={t("cancel")}
              />
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
}
